// Types and PURE presentation logic for the Apple Insights panel — the read-only engagement-metrics
// surface. No React, no FireCMS, no network in this file, so "what number does the panel show?" is
// answerable without reading any UI machinery.
//
// ==============================================================================================
// ⚠️ THIS PANEL READS A FIRESTORE PROJECTION. THERE IS NO BROWSER-CALLABLE BACKEND ROUTE, AND THERE
// IS NOT GOING TO BE ONE.
// ==============================================================================================
//
// An earlier version of this surface called `POST /internal/insights/report` through
// `apple_insights.fetch.ts` and said on screen that the route was "not registered yet". That
// expectation is WITHDRAWN, and the file is gone: the `/internal/*` routes are guarded by
// `x-internal-secret`, that secret cannot ship in a browser bundle (anything in this bundle is
// readable by anyone who opens dev tools), and the same guard fronts `POST /internal/locations`,
// `/brands`, `/media` and `/showcases` — so leaking it would open every internal WRITE endpoint to
// get one read. The old file's own header had already reasoned its way to that conclusion; it just
// assumed a route would arrive anyway.
//
// Instead a backend SCHEDULED JOB reads Apple Insights daily and writes a read-only, secret-free
// projection to Firestore, which this CMS reads with the Firebase ID token it already holds. Exactly
// the pattern `apple_grant_status` uses to keep `apple_org_grants` (encrypted OAuth tokens)
// unreadable from here.
//
// ⚠️ SOURCE OF THIS CONTRACT: the 2026-08 insights-projection brief. The backend module that writes
// it is not in the branch this was written against, so — per the citation convention this codebase
// keeps (Apple's own documents are not on any build machine either) — it is NOT cited by path. Keep
// the two in step by hand, the same way `apple_grant_status.types.ts` mirrors its projection.
// ⚠️ PENDING LIVE VERIFICATION AT STAGE-2.
//
// ⚠️ NO APPLE `file:line` CITATIONS APPEAR HERE, DELIBERATELY. The Apple documents are not on this
// machine (the backend records the full convention at the top of
// `functions/src/schemas/apple-resource.ts`), so every Apple claim below is attributed to the
// 2026-08 insights ticket brief instead of dressed up with a reference that could not be checked.

/** The Firestore collection holding the projection. Document id is the DearDoc `accountId`. */
export const APPLE_INSIGHTS_STATUS_PATH = "apple_insights_status";

/**
 * THE ONE NUMBER THIS PANEL EXISTS FOR — Apple's own count of taps on the booking CTA. Every other
 * part of the ABC integration exists to publish that button; this is Apple measuring whether it
 * works.
 *
 * ⚠️ THE PANEL DOES NOT DERIVE THE HERO NUMBER BY MATCHING THIS STRING. The projection carries
 * `appointmentTaps` precisely so nothing in the browser has to, and a typo here would then be
 * cosmetic instead of silently reporting "no bookings" forever. This constant survives for two
 * narrow uses: the hero tile's LABEL, and keeping the appointment row from being printed a second
 * time in the list below the tile (`secondaryMetrics`). If Apple ever renames the metric, the
 * consequence is that the row appears in that list as well as in the tile — visible and harmless —
 * rather than a number going missing.
 */
export const APPOINTMENT_METRIC_NAME = "PLACECARD_SCHEDULE_APPOINTMENT";

/**
 * Human labels for the metric names the 2026-08 brief listed.
 *
 * ⚠️ EXPLICITLY PARTIAL, AND THE PANEL MUST NOT ASSUME OTHERWISE. The brief's list ended in "…" and
 * the OpenAPI spec is not on disk, so this cannot be completed by reading. A metric name absent from
 * this map is NOT an error — `metricLabel` falls back to a readable form of the raw name, so a metric
 * Apple adds tomorrow appears in the panel (slightly less prettily) instead of vanishing or throwing.
 */
export const INSIGHTS_METRIC_LABELS: Record<string, string> = {
  PLACECARD_SCHEDULE_APPOINTMENT: "Appointments scheduled",
  PLACECARD_VIEW: "Place card views",
  PLACECARD_TAP_DIRECTION: "Directions tapped",
  PLACECARD_TAP_WEBSITE: "Website tapped",
  PLACECARD_TAP_CALL: "Calls tapped",
  PLACECARD_TAP_SHARE: "Shares tapped",
  PLACECARD_TAP_SHOWCASE: "Showcases tapped",
  PLACECARD_VIEW_MENU: "Menu views",
  PLACECARD_VIEW_AVAILABILITY: "Availability views",
  PLACECARD_VIEW_PRICING: "Pricing views",
  PLACECARD_GALLERY_ENGAGEMENT: "Gallery engagement",
  SEARCH_LOCATION_TAP_NAME: "Search — name tapped",
  SEARCH_LOCATION_TAP_CATEGORY: "Search — category tapped",
};

/**
 * One pre-summed metric row from the projection.
 *
 * ⚠️ `name` IS AN OPEN STRING AND MUST STAY ONE. Apple's metric vocabulary is partial by
 * construction (see the labels above), so this must never become a closed enum: one new name would
 * then either drop a row or fail a parse, and the panel would go blank for every client at once.
 *
 * ⚠️ `value: null` IS NOT ZERO. "Apple reported 0" and "we could not read a number" look identical
 * on a dashboard and mean opposite things — the first is a product problem to act on, the second is a
 * plumbing problem to fix. Rendering keeps them apart; do not "simplify" a null to 0 anywhere.
 */
export type InsightsMetricRow = {
  name: string;
  value: number | null;
};

/**
 * What the daily job managed to do on its last run.
 *
 * ⚠️ `NO_REPORTS` IS NOT `READ_FAILED`, and conflating them is the single most misleading thing this
 * panel could do. `NO_REPORTS` means Apple has nothing for this client yet — the EXPECTED state at
 * launch, because nothing in DearDoc currently asks Apple to create a report. `READ_FAILED` means our
 * job could not read Apple. Both render as an empty panel, so they must say different things.
 *
 * Typed as a union but READ DEFENSIVELY: it is a string in Firestore, the backend may add a value,
 * and an unrecognised one must render as an honest "this console does not recognise that" rather than
 * be silently treated as one of the four.
 */
export type InsightsProjectionStatus =
  | "REPORT_FOUND"
  | "NO_REPORTS"
  | "REPORT_UNREADABLE"
  | "READ_FAILED";

/** OUR classification of Apple's report state, computed by the BACKEND and only rendered here. */
export type InsightsReportReadiness = "READY" | "PENDING" | "FAILED" | "OTHER";

/**
 * One environment's bucket of the projection — `apple.<PRODUCTION|…>`.
 *
 * ⚠️ THE METRICS LIVE HERE, NOT AT THE TOP LEVEL, for the reason every `apple.<ENV>` bucket in this
 * console exists: a number read against Apple's DQE test host is not this client's engagement, and a
 * panel that mixed the two would be worse than one that showed nothing.
 */
export type InsightsEnvBucket = {
  /** See `InsightsProjectionStatus`. A string, because the backend owns the vocabulary. */
  status?: string | null;
  /** Provenance for an escalation: which Apple report these numbers came from. */
  reportId?: string | null;
  /** Apple's own state word for that report, raw. */
  reportState?: string | null;
  /** The backend's classification of `reportState`. `PENDING` means "still calculating", not zeros. */
  readiness?: InsightsReportReadiness | string | null;
  /**
   * ⚠️ APPLE'S RAW STRINGS, NOT TIMESTAMPS. The response format is unconfirmed against real Apple, so
   * the backend passes them through rather than coercing — and `formatInsightsTimestamp` renders the
   * raw string when it does not parse, never "Invalid Date".
   */
  reportCreatedDate?: string | null;
  reportCompletedDate?: string | null;
  /** THE HERO NUMBER, pre-extracted. `null` ⇒ unreadable ⇒ renders as "—". `0` is a real zero. */
  appointmentTaps?: number | null;
  /** Pre-summed and in a stable order. Rendered in the order given; this console does not re-sort. */
  metrics?: InsightsMetricRow[] | null;
  /** How many reports the search page held. `0` corroborates `NO_REPORTS`. */
  reportsFound?: number | null;
  /** When the NUMBERS were read. Shown next to the numbers. */
  observedAt?: Date | string | null;
  /**
   * When we last TRIED. ⚠️ On `READ_FAILED` this moves and `observedAt` does NOT — that pair is how
   * the panel says "showing Monday's figures; today's check failed". See `isShowingStaleFigures`.
   */
  lastReadAt?: Date | string | null;
};

/**
 * The projection document — `apple_insights_status/{accountId}`.
 *
 * ⚠️ READ-ONLY TO THIS CMS, AND UPDATED ONCE A DAY. Nothing in this console writes it, there is no
 * button that asks for a fresh read, and the panel must say on screen when the figures were observed
 * so a stale number is never read as a live one.
 */
export type AppleInsightsStatusEntity = {
  /** Also the document id — the DearDoc account, the same key `apple_grant_status` uses. */
  accountId: string;
  /** The Apple company the job read under. Resolved from the grant; never typed by anyone. */
  companyId?: string | null;
  apple?: { PRODUCTION?: InsightsEnvBucket | null } | null;
  /** When the projection document itself was last written. */
  updatedAt?: Date | string | null;
};

/**
 * The bucket this panel renders: `apple.PRODUCTION`, and only that.
 *
 * ⚠️ NO "MOST RECENTLY WRITTEN BUCKET" FALLBACK, unlike `resolveEnvBucket` on the onboarding console,
 * and the difference is deliberate. There, falling back tells staff which environment a WRITE landed
 * in — useful, and the environment is printed beside it. Here the value is a client-facing engagement
 * NUMBER: a DQE figure is a test-harness artifact, and showing one as "taps on this client's booking
 * button" would be a lie that looks exactly like the truth. Absent PRODUCTION ⇒ nothing to show.
 */
export const productionInsights = (
  values: Partial<AppleInsightsStatusEntity> | null | undefined,
): InsightsEnvBucket | null => values?.apple?.PRODUCTION ?? null;

/**
 * Which of the four statuses this is, or `null` for a value this build does not recognise.
 *
 * ⚠️ THE `null` BRANCH IS NOT DEAD CODE AND MUST NOT BE COLLAPSED INTO ONE OF THE FOUR. The field is
 * a string written by a backend that may add a status; guessing which of the four an unknown value
 * resembles is how a panel starts asserting "Apple has nothing for this client" about a state that
 * meant something else entirely.
 */
export const insightsStatusOf = (
  bucket: InsightsEnvBucket | null | undefined,
): InsightsProjectionStatus | null => {
  switch (bucket?.status) {
    case "REPORT_FOUND":
    case "NO_REPORTS":
    case "REPORT_UNREADABLE":
    case "READ_FAILED":
      return bucket.status;
    default:
      return null;
  }
};

/**
 * "The figures on screen are older than our last attempt to refresh them."
 *
 * ⚠️ THIS IS THE `READ_FAILED` STORY, AND IT IS THE ONE CASE WHERE A PANEL SHOWING NUMBERS IS ALSO
 * SHOWING A PROBLEM. `lastReadAt` moves on every attempt; `observedAt` only moves when numbers were
 * actually read. When they differ, the honest sentence is "showing the figures from <observedAt>, and
 * the check on <lastReadAt> failed" — not silence, and not a fresh-looking dashboard.
 *
 * Compared on the millisecond, and only when BOTH parse. A `lastReadAt` that cannot be read is not
 * evidence of staleness, so the answer is `false` rather than a warning nobody can act on.
 */
export const isShowingStaleFigures = (bucket: InsightsEnvBucket | null | undefined): boolean => {
  const observed = toMillis(bucket?.observedAt);
  const lastRead = toMillis(bucket?.lastReadAt);
  if (observed === null || lastRead === null) return false;
  return lastRead > observed;
};

/**
 * Does this bucket carry anything worth printing as a figure?
 *
 * ⚠️ IT ASKS ABOUT THE ROWS AS WELL AS THE HERO, and that is the whole reason it exists rather than
 * an inline `appointmentTaps !== undefined`. A bucket whose appointment count is unreadable (`null`)
 * but whose other metric rows are fine is exactly the case where hiding everything would be wrong:
 * the hero tile has an honest "—" for that, and the rows below it are real measurements.
 *
 * Used to decide whether a `READ_FAILED` panel — where the numbers are the PREVIOUS run's — has
 * anything to show at all.
 */
export const hasFigures = (bucket: InsightsEnvBucket | null | undefined): boolean =>
  typeof bucket?.appointmentTaps === "number" || (bucket?.metrics?.length ?? 0) > 0;

/**
 * A readable label for a metric name, including one Apple has only just invented.
 *
 * The fallback turns `PLACECARD_TAP_SOMETHING_NEW` into "Placecard tap something new" — not elegant,
 * but it names the thing, which is the entire requirement. Returning a placeholder like "Unknown
 * metric" would throw away the only information available.
 */
export const metricLabel = (name: string): string => {
  const known = INSIGHTS_METRIC_LABELS[name];
  if (known) return known;
  const words = name.toLowerCase().replace(/_/g, " ").trim();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : name;
};

/** True when the metric name is one the brief listed (so the panel can flag the rest as new). */
export const isKnownMetricName = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(INSIGHTS_METRIC_LABELS, name);

/**
 * The metric rows to render under the hero tile: everything the projection carries, with the
 * appointment row removed because it IS the tile.
 *
 * ⚠️ NOTHING IS DROPPED FOR BEING UNRECOGNISED, AND NOTHING IS DROPPED FOR HAVING NO NUMBER. An
 * unknown name renders through `metricLabel`'s humanised fallback; a `null` value renders as "—". A
 * filter on either would silently hide the one metric that turned out to matter.
 *
 * ⚠️ AND THE ORDER IS APPLE'S, VIA THE PROJECTION, NOT THIS FILE'S. The rows arrive pre-summed and in
 * a stable order; an earlier version of this surface sorted by value descending, which is a different
 * (and unstable) claim about what the reader should look at first.
 */
export const secondaryMetrics = (
  metrics: InsightsMetricRow[] | null | undefined,
): InsightsMetricRow[] =>
  (metrics ?? []).filter((metric) => metric.name !== APPOINTMENT_METRIC_NAME);

/** Metric names in this report that the brief did not list — surfaced as an observation, not an error. */
export const unknownMetricNames = (metrics: InsightsMetricRow[] | null | undefined): string[] =>
  Array.from(
    new Set(
      (metrics ?? []).map((metric) => metric.name).filter((name) => !isKnownMetricName(name)),
    ),
  ).sort();

/**
 * Thousands separators, because six-figure view counts are unreadable without them — and an em dash
 * for a number that is not there.
 *
 * ⚠️ `null` → "—" AND `0` → "0", NEVER THE OTHER WAY ROUND. This is the whole of the
 * unreadable-versus-zero distinction at the point of rendering, and it is one function so no caller
 * can implement it differently. `0` is a real, meaningful measurement.
 */
export const formatMetricValue = (value: number | null | undefined): string =>
  typeof value === "number" ? value.toLocaleString("en-US") : "—";

/**
 * Renders a timestamp defensively — for BOTH the projection's own Firestore dates and Apple's raw
 * report-date strings, which is why there is one function rather than two.
 *
 * ⚠️ IT NEVER RENDERS "Invalid Date". Apple's date format is unconfirmed against real Apple, so a
 * string that does not parse is shown AS ITSELF: ugly, honest, and diagnosable. Firestore timestamps
 * arrive as `Date` through the FireCMS data source and take the first branch.
 */
export const formatInsightsTimestamp = (
  value: Date | string | null | undefined,
): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toLocaleString("en-US");
  }
  if (value.trim().length === 0) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString("en-US");
};

/** Milliseconds for a comparison, or `null` when the value cannot be read as a time at all. */
const toMillis = (value: Date | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  const millis = parsed.getTime();
  return Number.isNaN(millis) ? null : millis;
};
